import { Component, Vue } from 'vue-property-decorator'
import { inject } from 'inversify-props'
import API from '@core/services/API'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppForm from '@core/components/AppForm.vue'
import { FormStructure } from '@master/model/structure/FormStructure'
import { IDateService } from '@master/services/IDateService'
import { CompanyDetailsMeta, header } from './model/CompanyDetailsMeta'
import { CompanyDetailsMetaList } from './model/CompanyDetailsMetaList'
import AppDialog from '@core/components/AppDialog.vue'

@Component({
  components: {
    AppDatatable,
    AppForm,
    AppDialog
  }
})
export class CompanyCodesListMixin extends Vue {
  @inject()
  protected dateService: IDateService

  @inject()
  protected $api: API

  protected companyDetailsMetaList: CompanyDetailsMetaList = null
  protected newCompanyDetails: CompanyDetailsMeta = new CompanyDetailsMeta()
  protected txtSearch = ''
  protected showModal = false
  protected isCreate = true
  protected errorMessage = ''

  formattedDate (unixTime: number | string) {
    return this.dateService.format(Number(unixTime), 'YYYY-MM-DD')
  }

  get list () {
    return !this.companyDetailsMetaList ? [] : this.companyDetailsMetaList.list.map(companyDetails => {
      return {
        ...companyDetails,
        companyDetailsEffectiveDate: companyDetails.companyDetailsEffectiveDate ? this.formattedDate(companyDetails.companyDetailsEffectiveDate) : null,
        emit: true
      }
    })
  }

  get columns () {
    return [
      { label: 'Company Name', field: 'companyDetailsName' },
      { label: 'Company Display Name', field: 'companyShortName' },
      { label: 'Company Code', field: 'companyDetailsCode' },
      { label: 'Effective Date', field: 'companyDetailsEffectiveDate' },
      { label: 'Status', field: 'companyDetailsStatus' }
    ]
  }

  get structure (): FormStructure<CompanyDetailsMeta> {
    return [
      {
        label: 'Company Name',
        key: 'companyDetailsName'
      },
      {
        label: 'Company Display Name',
        key: 'companyShortName'
      },
      {
        label: 'Company Code',
        key: 'companyDetailsCode'
      },
      {
        label: 'Effective Date',
        key: 'companyDetailsEffectiveDate',
        type: 'date'
      },
      {
        label: 'Status',
        key: 'companyDetailsStatus',
        options: [
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' }
        ]
      },
      {
        label: 'Remarks',
        key: 'companyDetailsRemarks',
        type: 'textarea',
        classes: ['colspan']
      }
    ]
  }

  addNew () {
    this.showModal = true
    this.isCreate = true
    this.errorMessage = ''
    this.newCompanyDetails = new CompanyDetailsMeta()
    this.newCompanyDetails.companyDetailsEffectiveDate = null
  }

  rowClick (row: CompanyDetailsMeta) {
    this.showModal = true
    this.isCreate = false
    this.errorMessage = ''
    Object.assign(this.newCompanyDetails, row)
    this.newCompanyDetails.companyDetailsEffectiveDate =
      this.newCompanyDetails.companyDetailsEffectiveDate
        ? new Date(this.newCompanyDetails.companyDetailsEffectiveDate).valueOf()
        : null
  }

  validate () {
    return Object.keys(this.newCompanyDetails)
      .filter((key) => key !== 'oid' && key !== 'companyDetailsRemarks')
      .every((key) => this.newCompanyDetails[key])
  }

  async save () {
    const valid = this.validate()
    this.errorMessage = ''
    if (!valid) {
      this.errorMessage = 'Please complete all required fields*'
      return
    }

    this.newCompanyDetails.companyDetailsEffectiveDate =
      this.newCompanyDetails.companyDetailsEffectiveDate
        ? new Date(this.newCompanyDetails.companyDetailsEffectiveDate).valueOf()
        : null

    /* eslint-disable @typescript-eslint/camelcase */
    const data = {
      company_details_name: this.newCompanyDetails.companyDetailsName,
      company_short_name: this.newCompanyDetails.companyShortName,
      company_details_code: this.newCompanyDetails.companyDetailsCode,
      company_details_effective_date: this.newCompanyDetails.companyDetailsEffectiveDate,
      company_details_status: this.newCompanyDetails.companyDetailsStatus,
      company_details_remarks: this.newCompanyDetails.companyDetailsRemarks
    }
    /* eslint-enable */

    await this.$api.companyDetails.save({
      _oid: this.isCreate ? 'new' : this.newCompanyDetails.oid,
      meta: JSON.stringify(data)
    })
    this.showModal = false
    await this.fetchData()
  }

  async fetchData () {
    const res = await this.$api.companyDetails.list({
      headers: JSON.stringify(header)
    })
    this.companyDetailsMetaList = new CompanyDetailsMetaList(res.data)
  }

  async mounted () {
    await this.fetchData()
  }
}
